import React, { useState } from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import type { PatientMedicationResponse } from '../../services/api';
import MedicationTableRow from './MedicationTableRow';
import {
  getMedicationDisplayName,
  isActuallyDiscontinued,
} from './medicationUtils';

export interface MedicationSelectionState {
  selectedIds: Set<string>;
  getEligibility: (medication: PatientMedicationResponse) => {
    eligible: boolean;
    reason?: string;
  };
  onToggle: (medicationId: string, checked: boolean) => void;
}

interface MedicationsTableProps {
  medications: PatientMedicationResponse[];
  onEdit: (medication: PatientMedicationResponse) => void;
  onDelete: (medication: PatientMedicationResponse) => void;
  onDiscontinue: (medicationId: string, date: Date) => Promise<void>;
  selection?: MedicationSelectionState;
}

type SortColumn = 'start_date' | 'discontinue_date' | 'medication';
type SortDirection = 'asc' | 'desc';

const MedicationsTable: React.FC<MedicationsTableProps> = ({
  medications,
  onEdit,
  onDelete,
  onDiscontinue,
  selection,
}) => {
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSortClick = (column: SortColumn) => {
    if (sortColumn !== column) {
      setSortColumn(column);
      setSortDirection('desc');
    } else if (sortDirection === 'desc') {
      setSortDirection('asc');
    } else {
      setSortColumn(null);
    }
  };

  const getSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) {
      return null;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className='h-4 w-4 ml-1' />
    ) : (
      <ArrowDown className='h-4 w-4 ml-1' />
    );
  };

  const sortedMedications = [...medications].sort((a, b) => {
    if (!sortColumn) {
      const aDiscontinued = isActuallyDiscontinued(a);
      const bDiscontinued = isActuallyDiscontinued(b);
      if (aDiscontinued !== bDiscontinued) {
        return aDiscontinued ? 1 : -1;
      }
      return (
        new Date(b.start_date).getTime() - new Date(a.start_date).getTime()
      );
    }

    let comparison = 0;
    switch (sortColumn) {
      case 'start_date':
        comparison =
          new Date(a.start_date).getTime() - new Date(b.start_date).getTime();
        break;
      case 'discontinue_date':
        if (a.discontinue_date && b.discontinue_date) {
          comparison =
            new Date(a.discontinue_date).getTime() -
            new Date(b.discontinue_date).getTime();
        } else if (a.discontinue_date) {
          comparison = -1;
        } else if (b.discontinue_date) {
          comparison = 1;
        } else {
          comparison = 0;
        }
        break;
      case 'medication': {
        const aName = getMedicationDisplayName(a).toLowerCase();
        const bName = getMedicationDisplayName(b).toLowerCase();
        comparison = aName.localeCompare(bName);
        break;
      }
    }

    return sortDirection === 'asc' ? comparison : -comparison;
  });

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {selection && <TableHead className='w-[44px]'></TableHead>}
          <TableHead className='w-[120px]'>Status</TableHead>
          <TableHead className='w-[130px]'>
            <button
              className='flex items-center hover:text-foreground transition-colors'
              onClick={() => handleSortClick('start_date')}
            >
              Start Date
              {getSortIcon('start_date')}
            </button>
          </TableHead>
          <TableHead className='w-[130px]'>
            <button
              className='flex items-center hover:text-foreground transition-colors'
              onClick={() => handleSortClick('discontinue_date')}
            >
              Discontinue
              {getSortIcon('discontinue_date')}
            </button>
          </TableHead>
          <TableHead>
            <button
              className='flex items-center hover:text-foreground transition-colors'
              onClick={() => handleSortClick('medication')}
            >
              Medication
              {getSortIcon('medication')}
            </button>
          </TableHead>
          <TableHead className='w-[80px]'></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedMedications.map(med => (
          <MedicationTableRow
            key={med.id}
            medication={med}
            onEdit={onEdit}
            onDelete={onDelete}
            onDiscontinue={onDiscontinue}
            selection={
              selection
                ? {
                    selected: selection.selectedIds.has(med.id),
                    ...selection.getEligibility(med),
                    onToggle: checked => selection.onToggle(med.id, checked),
                  }
                : undefined
            }
          />
        ))}
      </TableBody>
    </Table>
  );
};

export default MedicationsTable;
